// src/pages/Team/TeamMembers.js

import React, { useState, useEffect, useMemo } from "react";
import axios from "../../../utills/api";
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "../../../Components/Common/TableContainer";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DepartmentModal from "./CategoryModal/DepartmentModal";

const API_URL = "team";
const API_BASE = "department";

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  // const [filteredMembers, setFilteredMembers] = useState([]);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [departmentModal, setDepartmentModal] = useState(false);

  // ✅ Logged-in user
  const user = JSON.parse(localStorage.getItem("user"));
  const loggedRole = user?.role;
  const loggedDept = user?.department;

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    department: "",
    role: "",
    joiningDate: "",
    status: "Active",
  });

  // ================= FETCH =================

  const fetchMembers = async () => {
    try {
      const res = await axios.get(API_URL);
      let data = Array.isArray(res.data) ? res.data : [];

      // 🔥 APPLY ROLE-BASED FILTER
      if (loggedRole === "Manager") {
        data = data.filter(
          (member) =>
            member.department === loggedDept && // same department
            member.role !== "Manager" // ❌ exclude managers
        );
      }

      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.joiningDate || b.createdAt) -
          new Date(a.joiningDate || a.createdAt)
      );

      setMembers(sorted);
      // setFilteredMembers(sorted);
    } catch (error) {
      toast.error("Failed to fetch team members!");
      console.error("Error fetching team members:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_BASE}/department`);
      setDepartments(res.data || []);
    } catch (error) {
      toast.error("Failed to fetch departments!");
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchDepartments();
  }, []);

  // ================= SUBMIT =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        joiningDate: formData.joiningDate
          ? new Date(formData.joiningDate).toISOString()
          : null,
      };

      let res;

      if (isEditMode && selectedMember) {
        res = await axios.put(`${API_URL}/${selectedMember._id}`, payload);
        toast.success("Member updated successfully!");
      } else {
        res = await axios.post(API_URL, payload);
        toast.success("Member added successfully!");
      }

      const newMember = res.data;

      const updateList = (list) =>
        isEditMode
          ? list.map((m) => (m._id === newMember._id ? newMember : m))
          : [newMember, ...list];

      setMembers(updateList);


      setModal(false);
    } catch (error) {
      toast.error("Failed to save member!");
    }
  };


  const departmentOptions = useMemo(() => {
    return [
      "ALL",
      ...new Set(
        members
          .map((m) => m.department)
          .filter(Boolean)
      ),
    ];
  }, [members]);

  const roleOptions = useMemo(() => {
    return [
      "ALL",
      ...new Set(
        members
          .map((m) => m.role)
          .filter(Boolean)
      ),
    ];
  }, [members]);

  const statusOptions = useMemo(() => {
    return [
      "ALL",
      ...new Set(
        members
          .map((m) => m.status)
          .filter(Boolean)
      ),
    ];
  }, [members]);

  // ================= DELETE =================

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/${selectedMember._id}`);
      toast.success("Deleted!");

      setMembers((prev) =>
        prev.filter((m) => m._id !== selectedMember._id)
      );


      setDeleteModal(false);
    } catch (error) {
      toast.error("Delete failed!");
    }
  };

  const filteredMembers = useMemo(() => {
    let data = [...members];

    if (departmentFilter !== "ALL") {
      data = data.filter(
        (member) =>
          member.department === departmentFilter
      );
    }

    if (roleFilter !== "ALL") {
      data = data.filter(
        (member) =>
          member.role === roleFilter
      );
    }

    if (statusFilter !== "ALL") {
      data = data.filter(
        (member) =>
          member.status === statusFilter
      );
    }

    return data;
  }, [
    members,
    departmentFilter,
    roleFilter,
    statusFilter,
  ]);

  // ================= TABLE =================

  const columns = useMemo(
    () => [
      { header: "Name", accessorKey: "name" },
      { header: "Mobile", accessorKey: "mobile" },
      { header: "Department", accessorKey: "department" },
      { header: "Role", accessorKey: "role" },
      {
        header: "Joining Date",
        accessorFn: (row) =>
          row.joiningDate
            ? new Date(row.joiningDate).toLocaleDateString()
            : "-",
      },
      {
        header: "Status",
        accessorKey: "status",
      },
      {
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-soft-primary"
              onClick={() => openEditModal(row.original)}
            >
              <i className="ri-edit-line"></i>
            </button>

            <button
              className="btn btn-sm btn-soft-danger"
              onClick={() => {
                setSelectedMember(row.original);
                setDeleteModal(true);
              }}
            >
              <i className="las la-trash-alt"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // ================= MODALS =================

  const openAddModal = () => {
    setIsEditMode(false);

    setFormData({
      name: "",
      mobile: "",
      department:
        loggedRole === "Manager" ? loggedDept : "",
      role: "",
      joiningDate: "",
      status: "Active",
    });

    setModal(true);
  };

  const openEditModal = (member) => {
    setIsEditMode(true);
    setSelectedMember(member);

    setFormData({
      name: member.name,
      mobile: member.mobile,
      department: member.department,
      role: member.role || "",
      joiningDate: member.joiningDate?.split("T")[0],
      status: member.status,
    });

    setModal(true);
  };

  // ================= UI =================

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Teams" pageTitle="Team" />
          <Row>
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

                  {/* Left */}
                  <h5 className="mb-0">Team Lists</h5>

                  {/* Center Filters */}
                  <div className="d-flex gap-2 flex-wrap">

                    <Input
                      type="select"
                      style={{ width: "220px" }}
                      value={departmentFilter}
                      onChange={(e) =>
                        setDepartmentFilter(e.target.value)
                      }
                    >
                      {departmentOptions.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept === "ALL"
                            ? "All Departments"
                            : dept}
                        </option>
                      ))}
                    </Input>

                    <Input
                      type="select"
                      style={{ width: "180px" }}
                      value={roleFilter}
                      onChange={(e) =>
                        setRoleFilter(e.target.value)
                      }
                    >
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>
                          {role === "ALL"
                            ? "All Roles"
                            : role}
                        </option>
                      ))}
                    </Input>

                    <Input
                      type="select"
                      style={{ width: "180px" }}
                      value={statusFilter}
                      onChange={(e) =>
                        setStatusFilter(e.target.value)
                      }
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status === "ALL"
                            ? "All Status"
                            : status}
                        </option>
                      ))}
                    </Input>

                  </div>

                  {/* Right */}
                 <div className="d-flex gap-2">
  <button
    className="btn btn-success btn-sm"
    onClick={() => setDepartmentModal(true)}
  >
    + Department
  </button>

  <button
    className="btn btn-primary btn-sm"
    onClick={openAddModal}
  >
    + Add Member
  </button>
</div>

                </div>
              </CardHeader>

              <CardBody>

                <TableContainer
                  columns={columns}
                  data={filteredMembers}
                  customPageSize={10}
                  isPagination
                />
              </CardBody>
            </Card>
          </Row>

          {/* ADD / EDIT MODAL */}
          <Modal isOpen={modal} toggle={() => setModal(!modal)}>
            <ModalHeader toggle={() => setModal(!modal)}>
              {isEditMode ? "Edit Member" : "Add Member"}
            </ModalHeader>

            <ModalBody>
              <Form onSubmit={handleSubmit}>
                <div className="row">

                  <FormGroup className="col-md-6">
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup className="col-md-6">
                    <Label>Mobile</Label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) =>
                        setFormData({ ...formData, mobile: e.target.value })
                      }
                      required
                    />
                  </FormGroup>

                  {/* 🔥 DEPARTMENT LOCK */}
                  <FormGroup className="col-md-6">
                    <Label>Department</Label>
                    <Input
                      type="select"
                      value={formData.department}
                      disabled={loggedRole === "Manager"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          department: e.target.value,
                        })
                      }
                    >
                      <option value="">Select</option>
                      {departments.map((d) => (
                        <option key={d._id} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  {/* 🔥 ROLE CONTROL */}
                  <FormGroup className="col-md-6">
                    <Label>Role</Label>
                    <Input
                      type="select"
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value })
                      }
                    >
                      <option value="">Select Role</option>

                      {/* ❌ Hide Manager if logged user is Manager */}
                      {loggedRole !== "Manager" && (
                        <option>Manager</option>
                      )}

                      <option>Executive</option>
                      <option>Team Member</option>
                    </Input>
                  </FormGroup>

                  <FormGroup className="col-md-6">
                    <Label>Joining Date</Label>
                    <Input
                      type="date"
                      value={formData.joiningDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          joiningDate: e.target.value,
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup className="col-md-6">
                    <Label>Status</Label>
                    <Input
                      type="select"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value,
                        })
                      }
                    >
                      <option>Active</option>
                      <option>Block</option>
                    </Input>
                  </FormGroup>

                </div>

                <Button color="primary">
                  {isEditMode ? "Update" : "Save"}
                </Button>
              </Form>
            </ModalBody>
          </Modal>

          {/* DELETE MODAL */}
          <Modal isOpen={deleteModal}>
            <ModalHeader>Confirm Delete</ModalHeader>
            <ModalBody>
              <p>Are you sure?</p>
              <Button onClick={handleDelete} color="danger">
                Yes Delete
              </Button>
            </ModalBody>
          </Modal>

        </Container>
      </div>
      <DepartmentModal
      isOpen={departmentModal}
      toggle={() => setDepartmentModal(false)}
    />
    </React.Fragment>
  );
};

export default TeamMembers;